import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import decode from 'jwt-decode';
import { APP_NAME_PULSE_WEB } from 'solaris-lens-common-lib/src/constants';
import {
  login,
  logout,
  forgot,
  recover,
  passwordReset,
  refresh
} from 'solaris-lens-common-lib/src/services/auth';
import delay from 'solaris-lens-common-lib/src/utils';

import {
  fetchAuditableElements,
  clearAuditableElements
} from './auditableElementsSlice';
import { AuthState, TokenResponse, ApiError, RefreshState } from './types';
import {
  fetchUserProfile,
  clearUserProfile,
  clearStatus as clearUserStatus
} from './userProfileSlice';
import { getErrorObject } from './utils';
import { AppDispatch } from '../store';

export const signInAndFill =
  ({
    username,
    password,
    poolName
  }: {
    username: string;
    password: string;
    poolName: string;
  }) =>
  async (dispatch: AppDispatch) => {
    const result = await dispatch(
      fetchSignIn({
        username,
        password,
        poolName
      })
    );

    if (fetchSignIn.fulfilled.match(result)) {
      await dispatch(fetchUserProfile({}));
      dispatch(clearUserStatus());
    }
  };

export const refreshSign =
  ({
    refreshToken,
    username,
    poolName
  }: {
    refreshToken: string;
    username: string;
    poolName: string;
  }) =>
  async (dispatch: AppDispatch) => {
    const result = await dispatch(
      fetchRefresh({
        refreshToken,
        username,
        poolName
      })
    );

    if (fetchRefresh.fulfilled.match(result)) {
      dispatch(
        fetchAuditableElements({
          applicationName: APP_NAME_PULSE_WEB
        })
      );
    }
  };

export const signOidcAndFill =
  (params: AuthState) => async (dispatch: AppDispatch) => {
    dispatch(signIn(params));
    await dispatch(fetchUserProfile({}));
  };

export const clearOnLogOut = () => async (dispatch: AppDispatch) => {
  await dispatch(signOut());
  await dispatch(clearUserProfile());
  await dispatch(clearAuditableElements());
};

export const fetchSignIn = createAsyncThunk(
  'auth/signIn',
  async (
    {
      username,
      password,
      poolName
    }: { username: string; password: string; poolName: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await login({ username, password, poolName });
      return { ...response, poolName, isOidcClient: false };
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

export const fetchSignOut = createAsyncThunk(
  'auth/signOut',
  async (_: void, { getState, rejectWithValue }) => {
    const state = getState() as { auth: TokenResponse };
    const token = state.auth.accessToken;
    const refreshToken = state.auth.refreshToken;
    const poolName = state.auth.poolName;
    try {
      const response = await logout(token, refreshToken, poolName);
      return response;
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

export const fetchForgot = createAsyncThunk(
  'auth/forgot',
  async (
    { username, poolName }: { username: string; poolName: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await forgot({ username, poolName });
      return response;
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

export const fetchRecover = createAsyncThunk(
  'auth/recover',
  async (
    {
      code,
      username,
      newPassword,
      reNewPassword,
      poolName
    }: {
      code: string;
      username: string;
      newPassword: string;
      reNewPassword: string;
      poolName: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await recover({
        code,
        username,
        newPassword,
        reNewPassword,
        poolName
      });
      return response;
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

export const fetchPasswordReset = createAsyncThunk(
  'auth/passwordReset',
  async (
    {
      username,
      password,
      newPassword,
      reNewPassword,
      poolName
    }: {
      username: string;
      password: string;
      newPassword: string;
      reNewPassword: string;
      poolName: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await passwordReset({
        username,
        password,
        newPassword,
        reNewPassword,
        poolName
      });
      await delay(3000);
      return response;
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

export const fetchRefresh = createAsyncThunk(
  'auth/refresh',
  async (
    {
      refreshToken,
      username,
      poolName
    }: { refreshToken: string; username: string; poolName: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await refresh(refreshToken, username, poolName);
      return response;
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

export const initialStateAuth: AuthState = {
  accessToken: '',
  tokenPayload: {
    auth_time: 0,
    client_id: '',
    cognitoGroups: [],
    event_id: '',
    exp: 0,
    iat: 0,
    iss: '',
    jti: '',
    scope: '',
    sub: '',
    token_use: '',
    username: ''
  },
  tokenType: '',
  expiresIn: 0,
  idToken: '',
  issuedAt: 0,
  status: {
    code: 0,
    message: '',
    description: ''
  },
  error: { description: '', code: 0 },
  data: {
    username: '',
    password: ''
  },
  refreshToken: '',
  username: '',
  isOidcClient: false,
  poolName: ''
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: initialStateAuth,
  reducers: {
    signIn: (state, action: PayloadAction<AuthState>) => {
      state.status = action.payload.status;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.tokenPayload = decode(action.payload.accessToken);
      state.tokenType = action.payload.tokenType;
      state.expiresIn = action.payload.expiresIn;
      state.idToken = action.payload.idToken ?? '';
      state.issuedAt = action.payload.issuedAt;
      state.data = action.payload.data;
      state.username = action.payload.username;
      state.isOidcClient = action.payload.isOidcClient;
      state.poolName = action.payload.poolName;
      state.error = action.payload.error;
    },
    signOut: () => initialStateAuth,
    addUserToAuth: (state, action: PayloadAction<AuthState>) => {
      state.username = action.payload.username;
    },
    changeUserPassword: (state, action: PayloadAction<AuthState>) => {
      if (state.data) {
        state.data.username = action.payload.data?.username;
        state.data.password = action.payload.data?.password;
      }
      state.error = initialStateAuth.error;
    },
    clearForm: state => {
      state.username = initialStateAuth.username;
      state.status = initialStateAuth.status;
      state.error = initialStateAuth.error;
    },
    clearStatus: state => {
      state.status = initialStateAuth.status;
      state.error = initialStateAuth.error;
    },
    refreshSignIn: (state, action: PayloadAction<RefreshState>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken ?? '';
      state.expiresIn = action.payload.expiresIn;
      state.idToken = action.payload.idToken ?? '';
      state.tokenPayload = decode(action.payload.accessToken);
    },
    restoreToken: state => {
      state.accessToken = '';
      state.refreshToken = '';
    }
  },
  extraReducers: builder => {
    builder
      // Sign In
      .addCase(fetchSignIn.pending, state => {
        state.status = { code: 1, message: 'loading', description: '' };
        state.error = initialStateAuth.error;
      })
      .addCase(fetchSignIn.fulfilled, (state, action) => {
        state.error = initialStateAuth.error;
        state.accessToken = action.payload?.accessToken;
        state.refreshToken = action.payload?.refreshToken;
        state.tokenPayload = action.payload?.tokenPayload;
        state.tokenType = action.payload?.tokenType;
        state.expiresIn = action.payload?.expiresIn;
        state.issuedAt = action.payload?.issuedAt;
        state.poolName = action.payload?.poolName;
        state.status = initialStateAuth.status;
      })
      .addCase(
        fetchSignIn.rejected,
        (state, action: ReturnType<typeof fetchSignIn.rejected>) => {
          const payload = action.payload as ApiError;
          state.status = { code: 2, message: 'failed', description: '' };
          state.error = {
            description: payload.technicalMessage,
            code: payload.code
          };
        }
      )

      // Sign Out
      .addCase(fetchSignOut.pending, state => {
        state.status = { code: 1, message: 'loading', description: '' };
        state.error = initialStateAuth.error;
      })
      .addCase(fetchSignOut.fulfilled, () => initialStateAuth)
      .addCase(fetchSignOut.rejected, (state, action) => {
        const payload = action.payload as ApiError;
        state.status = { code: 2, message: 'failed', description: '' };
        state.error = {
          description: payload.description,
          code: payload.code
        };
      })

      // Fetch Forgot
      .addCase(fetchForgot.pending, state => {
        state.status = { code: 1, message: 'loading', description: '' };
        state.error = initialStateAuth.error;
      })
      .addCase(fetchForgot.fulfilled, (state, action) => {
        Object.assign(state, action.payload);
      })
      .addCase(fetchForgot.rejected, (state, action) => {
        const payload = action.payload as ApiError;
        state.status = { code: 2, message: 'failed', description: '' };
        state.error = {
          description: payload.technicalMessage,
          code: payload.code
        };
      })

      // Fetch Recover
      .addCase(fetchRecover.pending, state => {
        state.status = { code: 1, message: 'loading', description: '' };
        state.error = initialStateAuth.error;
      })
      .addCase(fetchRecover.fulfilled, (state, action) => {
        Object.assign(state, action.payload);
        state.error = initialStateAuth.error;
      })
      .addCase(fetchRecover.rejected, (state, action) => {
        const payload = action.payload as ApiError;
        state.status = { code: 2, message: 'failed', description: '' };
        state.error = {
          description: payload.technicalMessage,
          code: payload.code
        };
      })

      // Fetch Password Reset
      .addCase(fetchPasswordReset.pending, state => {
        state.status = { code: 1, message: 'loading', description: '' };
        state.error = initialStateAuth.error;
      })
      .addCase(fetchPasswordReset.fulfilled, (state, action) => {
        Object.assign(state, action.payload);
        state.data = initialStateAuth.data;
        state.error = initialStateAuth.error;
      })
      .addCase(fetchPasswordReset.rejected, (state, action) => {
        const payload = action.payload as ApiError;
        state.status = { code: 2, message: 'failed', description: '' };
        state.error = {
          description: payload.technicalMessage || payload.description,
          code: payload.code
        };
      })

      // Fetch Refresh
      .addCase(fetchRefresh.pending, state => {
        state.status = { code: 1, message: 'loading', description: '' };
        state.error = initialStateAuth.error;
      })
      .addCase(fetchRefresh.fulfilled, (state, action) => {
        state.status = initialStateAuth.status;
        state.error = initialStateAuth.error;
        state.accessToken = action.payload.accessToken;
        state.tokenPayload = action.payload.tokenPayload;
        state.tokenType = action.payload.tokenType;
        state.expiresIn = action.payload.expiresIn;
        state.issuedAt = action.payload.issuedAt;
      })
      .addCase(fetchRefresh.rejected, (state, action) => {
        Object.assign(state, initialStateAuth);
        const payload = action.payload as ApiError;
        state.status = { code: 401, message: 'failed', description: '' };
        state.error = {
          description: payload.technicalMessage,
          code: payload.code
        };
      });
  }
});

// Action creators are generated for each case reducer function
export const {
  signIn,
  signOut,
  addUserToAuth,
  changeUserPassword,
  refreshSignIn,
  restoreToken,
  clearForm,
  clearStatus
} = authSlice.actions;
export default authSlice.reducer;
